import React, { FC, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { Dialog, useMediaQuery, useTheme, IconButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useConfirm } from 'material-ui-confirm';
import CancelIcon from '@mui/icons-material/Cancel';
import PostCatchBody from '../../../../../Models/CatchRegistrationModels/PostCatchBody';
import CatchRegistration from '../../../../../Models/CatchRegistrationModels/CatchRegistration';
import CatchCombination from '../../../../../Models/CatchRegistrationModels/CatchCombination';
import FishingArea from '../../../../../Models/CatchRegistrationModels/FishingArea';
import StorageMethod from '../../../../../Models/CatchRegistrationModels/StorageMethod';
import Destiny from '../../../../../Models/CatchRegistrationModels/Destiny';
import FishingStock from '../../../../../Models/CatchRegistrationModels/FishingStock';
import Condition from '../../../../../Models/CatchRegistrationModels/Condition';
import Gear from '../../../../../Models/WeightNoteModels/Gear';
import WeightNote from '../../../../../Models/WeightNoteModels/WeightNote';
import { MobilePaperComponent, PaperComponent } from '../../../../../SharedComponents/Paper/CustomPaper';
import CatchRegistrationDefaults from '../../../../../Constants/CatchRegistrationDefaults';
import CatchRegistrationCatch from '../CatchRegistrationCatch/CatchRegistrationCatch';
import CatchRegistrationWeight from '../CatchRegistrationWeight/CatchRegistrationWeight';
import WeightNoteTypes from '../../../../../Constants/WeightNoteTypes';

interface CatchRegistrationSplitModalProps {
  open: boolean;
  /** original item being split; only used to read splitWeight (limit), NOT as state for the form */
  weightItem: CatchRegistration;
  handleClose: () => void;
  /** expects a PostCatchBody (ids + numeric fields) */
  handleSplit: (formData: PostCatchBody) => Promise<void>;
  catchCombinations: CatchCombination[] | null;
  fishAreas: FishingArea[];
  storageMethods: StorageMethod[];
  destinies: Destiny[];
  fishingStocks: FishingStock[];
  conditions: Condition[];
  gears: Gear[];
  selectedWeightNote: WeightNote;
  showSnackbar: (message: string, severity: string) => void;
}

const CatchRegistrationSplitModal: FC<CatchRegistrationSplitModalProps> = (props) => {
  // Single source of truth for the form
  const [formData, setFormData] = useState<PostCatchBody>({});
  const [loading, setLoading] = useState(false);

  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const confirm = useConfirm();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // 🔹 Stock input focus management (same pattern as main modal)
  const stockInputRef = useRef<HTMLInputElement | null>(null);
  const [stockFocusTrigger, setStockFocusTrigger] = useState(0);

  const requestStockFocus = useCallback(() => {
    setStockFocusTrigger((prev) => prev + 1);
  }, []);

  // Focus stock when the split modal opens
  useEffect(() => {
    if (props.open) {
      requestStockFocus();
    }
  }, [props.open, requestStockFocus]);

  // Fast lookups (ids -> objects) for building a view model for children
  const stockById   = useMemo(() => new Map(props.fishingStocks.map(x => [x.id, x])), [props.fishingStocks]);
  const areaById    = useMemo(() => new Map(props.fishAreas.map(x => [x.id, x])), [props.fishAreas]);
  const storageById = useMemo(() => new Map(props.storageMethods.map(x => [x.id, x])), [props.storageMethods]);
  const destById    = useMemo(() => new Map(props.destinies.map(x => [x.id, x])), [props.destinies]);
  const condById    = useMemo(() => new Map(props.conditions.map(x => [x.id, x])), [props.conditions]);
  const whaleFishingStockIds = useMemo(() => new Set([
    CatchRegistrationDefaults.FISHING_STOCK_FIN_WHALE_ID,
    CatchRegistrationDefaults.FISHING_STOCK_MINKE_WHALE_ID
  ]), []);
  const selectedFishingStock = useMemo(() => {
    const stockId = formData.fishStockId ?? props.weightItem.fishingStock?.id;
    return stockId ? stockById.get(stockId) ?? props.weightItem.fishingStock : undefined;
  }, [formData.fishStockId, props.weightItem.fishingStock, stockById]);
  const showAnimalCount = selectedFishingStock?.id !== undefined &&
    whaleFishingStockIds.has(selectedFishingStock.id);

  // Build a CatchRegistration-shaped view purely from formData (no dependency on props.weightItem)
  const weightView: CatchRegistration | undefined = useMemo(() => {
    // If user hasn’t picked anything yet, return undefined so children use their own defaults.
    const anyPicked =
      formData.fishStockId ||
      formData.fishingAreaId ||
      formData.storageMethodId ||
      formData.destinyId ||
      formData.conditionId ||
      formData.weight ||
      formData.sampleWeight;

    if (!anyPicked) return undefined;

    const fishingStock  = formData.fishStockId    ? stockById.get(formData.fishStockId)      : undefined;
    const fishingArea   = formData.fishingAreaId  ? areaById.get(formData.fishingAreaId)     : undefined;
    const storageMethod = formData.storageMethodId? storageById.get(formData.storageMethodId): undefined;
    const destiny       = formData.destinyId      ? destById.get(formData.destinyId)         : undefined;
    const condition     = formData.conditionId    ? condById.get(formData.conditionId)       : undefined;

    // Not enough picked to show a coherent object? Let children keep defaults.
    if (!fishingStock || !fishingArea || !storageMethod || !destiny || !condition) {
      return undefined;
    }

    const weight       = formData.weight ?? 0;
    const sampleWeight = formData.sampleWeight ?? 0;
    const icePct       = formData.icePercentage ?? 0;

    return {
      id: -1,
      fishingArea,
      storageMethod,
      vehicle: undefined,
      fishingStock,
      condition,
      deductions: [],
      underKilogram: formData.isUnderKilogram ?? false,
      hasDeviation: false,
      destiny,
      weight,
      totalAmountSample: formData.totalAmountSample ?? 0,
      sampleWeight,
      samplePackagingUnits: 0,
      samplePackagingQuantity: 0,
      sampleAvgWeight: 0,
      animalCount: formData.animalCount ?? null,
      carDeduction: formData.vehicleDeduction ?? 0,
      deduction: formData.deduction ?? 0,
      icePercentage: icePct,
      declaredIcePercentage: formData.declaredIcePercentage ?? 0,
      quantity: weight,
      unGuttedQuantity: formData.unGuttedQuantity ?? 0,
      userComment: formData.userComment ?? '',
    };
  }, [formData, stockById, areaById, storageById, destById, condById]);

  // Children will call this; we only update formData
  const handleFormChange = (patch: PostCatchBody) => {
    setFormData(prev => ({ ...prev, ...patch }));
  };

  // Validation from formData
  const isProductNote = props.selectedWeightNote.weightNoteType.id === WeightNoteTypes.PRODUCT_ID;
  const hasWeightOk   = isProductNote ? (formData.sampleWeight ?? 0) > 0 : (formData.weight ?? 0) > 0;

  const isValid =
    !!formData.fishStockId &&
    !!formData.fishingAreaId &&
    !!formData.storageMethodId &&
    !!formData.destinyId &&
    !!formData.conditionId &&
    hasWeightOk;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isValid && !loading) {
        e.preventDefault();
        submitButtonRef.current?.click();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isValid, loading]);

  const handleSubmit = async () => {
    if (!isValid) return;
    try {
      setLoading(true);
      const submitData: PostCatchBody = showAnimalCount
        ? formData
        : { ...formData, animalCount: undefined, shouldClearAnimalCount: undefined };
      await props.handleSplit(submitData);
      props.showSnackbar('Vigtun skipt!', 'success');
      setFormData({});
      // 🔹 After a successful split, focus stock again for the next split
      requestStockFocus();
      setLoading(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setLoading(false);
      if (error?.response?.data && typeof error.response.data !== 'object') {
        props.showSnackbar(error.response.data, 'error');
      } else {
        props.showSnackbar('Óvænt villa kom upp!', 'error');
      }
    }
  };

  const closeHandler = () => {
    if (loading) return;
    const hasChanges = Object.keys(formData).length > 5;
    if (!hasChanges) {
      setFormData({});
      props.handleClose();
      return;
    }
    confirm({
      title: 'Villtu loka glugganum?',
      description: 'Þú ert með óvistaðar breytingar.',
      confirmationText: 'Loka',
      cancellationText: 'Hætta við',
      confirmationButtonProps: { variant: 'contained', color: 'primary' },
      cancellationButtonProps: { variant: 'outlined' },
    })
      .then(() => {
        setFormData({});
        props.handleClose();
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {});
  };

  return (
    <Dialog
      open={props.open}
      onClose={closeHandler}
      disableEscapeKeyDown={loading}
      PaperComponent={isMobile || isTablet ? MobilePaperComponent : PaperComponent}
      aria-labelledby="split-dialog-title"
      maxWidth="sm"
    >
      <div
        id="split-dialog-title"
        style={{
          fontSize: '1.5em',
          fontWeight: 'bold',
          lineHeight: '1.5em',
          margin: 0,
          cursor: 'move',
        }}
      >
        Skipta vigt
        <IconButton
          size="large"
          sx={{ position: 'absolute', top: 0, right: 0 }}
          onClick={closeHandler}
        >
          <CancelIcon fontSize="large" />
        </IconButton>
      </div>

      <div>
        <CatchRegistrationCatch
          disabled={loading}
          handleFormChange={handleFormChange}
          formData={formData}
          stockInputRef={stockInputRef}
          stockFocusTrigger={stockFocusTrigger}
          allowedCombinations={props.catchCombinations}
          weight={weightView}
          selectedWeightNote={props.selectedWeightNote}
          fishAreas={props.fishAreas}
          storageMethods={props.storageMethods}
          destinies={props.destinies}
          fishingStocks={props.fishingStocks}
          conditions={props.conditions}
          gears={props.gears}
          containsExported={
            props.selectedWeightNote.weights?.some(
              (w) => w.destiny?.id === CatchRegistrationDefaults.DESTINY_EXPORTED_ID
            ) ?? false
          }
        />

        <CatchRegistrationWeight
          formData={formData}
          handleFormChange={handleFormChange}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          setIcePercentageHasChanged={() => {}}
          weightItem={weightView}
          disabled={props.selectedWeightNote.isClosed}
          selectedWeightNote={props.selectedWeightNote}
          isExported={
            formData.destinyId === CatchRegistrationDefaults.DESTINY_EXPORTED_ID ||
            (!formData.destinyId &&
              props.weightItem?.destiny?.id === CatchRegistrationDefaults.DESTINY_EXPORTED_ID)
          }
          stockIsScallops={
            formData.fishStockId === CatchRegistrationDefaults.FISHING_STOCK_SCALLOPS_ID ||
            (!formData.fishStockId &&
              props.weightItem?.fishingStock?.id === CatchRegistrationDefaults.FISHING_STOCK_SCALLOPS_ID)
          }
          showAnimalCount={showAnimalCount}
          splitWeight={props.weightItem?.weight}
        />
        <div style={{ width: '100%', marginTop: '1em' }}>
          <LoadingButton
            fullWidth
            size="small"
            onClick={handleSubmit}
            loading={loading}
            disabled={loading || !isValid}
            color="primary"
            variant="contained"
            ref={submitButtonRef}
          >
            Skipta vigt
          </LoadingButton>
        </div>
      </div>
    </Dialog>
  );
};

export default CatchRegistrationSplitModal;
